import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Form,
  FormFeedback,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import * as Yup from "yup";

import { CreateContactRequest, ParsedError } from "../../interfaces";
import {
  useGetContactQuery,
  useAddContactMutation,
  useUpdateContactMutation,
} from "../../store/api/contactApi";
import { ActivityButton, AppPhoneInput } from "../../components/Common";
import { ErrorHandlingTypeEnum } from "../../enums";

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: (event: any) => void;
};
export const AddEditContactModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
}) => {
  const { t } = useTranslation();
  const [request] = useState<CreateContactRequest>();

  const {
    data: contact,
    error: errorGet,
    isLoading: isLoadingContact,
    isError: isErrorGet,
  } = useGetContactQuery(id!, {
    skip: !id,
  });

  const [
    addContact,
    { isLoading: isLoadingAdd, error: errorAdd, isError: isErrorAdd },
  ] = useAddContactMutation();

  const [
    updateContactMutate,
    { isLoading: isLoadingUpdate, error: errorUpdate, isError: isErrorUpdate },
  ] = useUpdateContactMutation();

  //#region form
  const form = useFormik<CreateContactRequest>({
    enableReinitialize: true,
    initialValues: {
      name: {
        en: request?.name.en || "",
        ar: request?.name.ar || "",
      },
      countryCode: request?.countryCode || "",
      phoneNumber: request?.phoneNumber || "",
    },
    validationSchema: Yup.object({
      name: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "englishValue",
            }),
          ),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(
            t("isNotEmpty", {
              displayName: "arabicValue",
            }),
          ),
      }),
      countryCode: Yup.string().required(),
      phoneNumber: Yup.string()
        .min(8, ({ min }) =>
          t("minLength", { minLength: min, displayName: "phoneNumber" }),
        )
        .matches(/^[0-9]+$/, t("digitsOnlyPhoneNumber"))
        .max(12, ({ max }) =>
          t("maxLength", { maxLength: max, displayName: "phoneNumber" }),
        )
        .required(
          t("isNotEmpty", {
            displayName: "phoneNumber",
          }),
        ),
    }),
    onSubmit: async (values) => {
      try {
        if (id) {
          await updateContactMutate({ id, data: values }).unwrap();
        } else {
          await addContact(values).unwrap();
        }
        onClose();
      } catch (e) {}
    },
  });
  //#endregion

  //#region effects
  const onClose = () => {
    form.resetForm();
    onCloseClick(!show);
  };

  useEffect(() => {
    if (id && contact) {
      form.setValues({
        name: contact.name,
        countryCode: contact.countryCode,
        phoneNumber: contact.phoneNumber,
      });
    }
  }, [contact, isLoadingContact, id]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingAdd && isErrorAdd) _error = errorAdd as ParsedError;
    if (!isLoadingUpdate && isErrorUpdate) _error = errorUpdate as ParsedError;
    if (_error?.type == ErrorHandlingTypeEnum.FORM) {
      form.setErrors(_error.error);
    }
  }, [isLoadingAdd, isLoadingUpdate]);

  //#endregion

  return (
    <Modal
      id="addEdit"
      isOpen={show}
      toggle={() => {
        onClose();
      }}
      centered
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
          return false;
        }}
        action="#"
      >
        <ModalHeader
          className="p-3"
          toggle={() => {
            onClose();
          }}
        >
          {id
            ? t("editItem", { item: "Contact" })
            : t("addItem", { item: "Contact" })}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label htmlFor="name.en" className="form-label">
              {t("personNameInEnglish")}
              <span className="text-danger">*</span>
            </label>
            <Input
              disabled={!!errorGet}
              autoComplete="off"
              id="name.en"
              name="name.en"
              value={form.values?.name.en}
              type="text"
              className="form-control"
              placeholder={t("enterInEnglish")}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={
                form.touched.name?.en && form.errors.name?.en ? true : false
              }
            />
            {form.touched.name?.en && form.errors.name?.en ? (
              <FormFeedback type="invalid">
                {form.errors.name?.en as string}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <label htmlFor="name.ar" className="form-label">
              {t("personNameInArabic")}
              <span className="text-danger">*</span>
            </label>
            <Input
              style={{ fontFamily: "aljannat", direction: "rtl" }}
              disabled={!!errorGet}
              autoComplete="off"
              auto
              id="name.ar"
              name="name.ar"
              value={form.values.name?.ar}
              type="text"
              className="form-control"
              placeholder={t("enterInArabic")}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={
                form.touched.name?.ar && form.errors.name?.ar ? true : false
              }
            />
            {form.touched.name?.ar && form.errors.name?.ar ? (
              <FormFeedback type="invalid">
                {form.errors.name?.ar as string}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <label htmlFor="name.ar" className="form-label">
              {t("phoneNumber")}
              <span className="text-danger">*</span>
            </label>
            <AppPhoneInput
              onTouch={() => form.setFieldTouched("phoneNumber")}
              value={
                form.values.countryCode && form.values.phoneNumber
                  ? `+${form.values.countryCode}${form.values.phoneNumber}`
                  : ""
              }
              onChange={({ countryCode, number, isValid }) => {
                if (isValid) {
                  form.setFieldError("phoneNumber", undefined);
                } else {
                  form.setFieldError("phoneNumber", "invalid phone number");
                }
                form.setFieldValue("countryCode", countryCode);
                form.setFieldValue("phoneNumber", number);
              }}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Link
            to={"#"}
            onClick={onClose}
            className="btn btn-link link-success fw-medium"
          >
            <i className="ri-close-line me-1 align-middle"></i> {t("close")}
          </Link>
          <ActivityButton
            title="save"
            inProgressTitle="saving"
            color="success"
            className="btn btn-success"
            type="submit"
            inProgress={isLoadingAdd || isLoadingUpdate}
            disabled={isLoadingAdd || isLoadingUpdate || isErrorGet}
          ></ActivityButton>
        </ModalFooter>
      </Form>
    </Modal>
  );
};
